import { DeleteStages } from 'constants/deleteStages';

import { useStagedDelete } from 'hooks/useStagedDelete';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { queryClient } from 'providers/queryClient';
import { useNotify } from 'react-admin';
import { CancelButton } from 'shared/mui/Buttons';
import { DeleteStageDialog } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialog';
import { DeleteStageDialogDenyReason } from 'shared/mui/Dialogs/DeleteStageDialog/DeleteStageDialogDenyReason';
import { Typography } from 'shared/mui/Typography';
import { TypographyBold } from 'shared/mui/Typography/TypographyBold';
import { MerchantAccount } from 'types/embedded/merchantAccount';

type Props = {
  record?: MerchantAccount;
  open: boolean;
  onClose: (record?: MerchantAccount) => void;
};

export const MerchantShowAccountsDeleteDialog = ({ onClose, open, record }: Props) => {
  const translate = useTypedTranslate();
  const notify = useNotify();

  const { deleteOneRecord, denyDeleteReason, stage, setStage } = useStagedDelete({
    record,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['merchant_accounts'] });
      onClose();
      setStage(DeleteStages.Initial);
      notify(translate('app.merchantAccounts.merchantAccountDeletedSuccessfully'), {
        type: 'success',
      });
    },
  });

  const handleCloseDeleteDialog = () => {
    setStage(DeleteStages.Initial);
    onClose(record);
  };

  return (
    <DeleteStageDialog
      deleteOneRecord={deleteOneRecord}
      details={
        <Typography>
          {translate('app.merchantAccounts.accountDelete1')}{' '}
          <TypographyBold fontSize={16} text={record?.name || ''} />{' '}
          {translate('app.merchantAccounts.accountDelete2')}
        </Typography>
      }
      onClose={handleCloseDeleteDialog}
      open={open}
      stage={stage}
      title={translate('app.merchantAccounts.deleteAccount')}
    >
      <DeleteStageDialogDenyReason reason={denyDeleteReason}>
        <CancelButton
          key="history-back-button"
          label={translate('app.buttons.close')}
          onClick={handleCloseDeleteDialog}
          variant="outlined"
        />
      </DeleteStageDialogDenyReason>
    </DeleteStageDialog>
  );
};
